/**
 * #5068 — hand-written drawing programs: the whiteboard demos (dev harness), the sandbox's reference programs (a
 * program streamed statement by statement must draw exactly what it draws run whole) and, later, worked examples for
 * the program prompt. Each body is plain JavaScript in the form a model writes: top-level card() and beat() calls, with
 * part() and joint() inside beats. Coordinates are on the 960 × 540 sheet.
 */
import type { DrawingProgramApi } from './drawingProgram';

export type DrawingSampleProgram = (api: DrawingProgramApi) => void;

const engine: DrawingSampleProgram = ({ card, beat, part, joint }) => {
  card('Inside one engine cylinder', { subtitle: 'Cut open · four strokes, two crank turns', takeaway: 'Suck, squeeze, bang, blow', period: 3 });
  beat('parts', "Here's one cylinder of a car engine, cut open so you can see inside.", () => {
    part('cylinder', 'cylinder', { x: 430, y: 240, size: 150, params: { depth: 1.8 }, label: 'Cylinder' });
    part('piston', 'piston', { x: 430, y: 215, size: 146, params: { height: .6 }, label: 'Piston' });
    part('crank', 'crank', { x: 430, y: 440, size: 110, params: { throw: .4 }, label: 'Crankshaft' });
    part('link', 'rod', { params: { from: [430, 396], to: [430, 228], width: 18 }, label: 'Connecting rod' });
    joint({ kind: 'crankSlider', crank: 'crank', slider: 'piston', link: 'rod', radius: 44, rodLength: 168, axis: -90, ratio: 1, phase: 0 });
  });
  beat('valves', "On top, a camshaft turning at half the crank's speed pushes two valves open in turn.", () => {
    part('cam', 'intakeCam', { x: 395, y: 36, size: 46, rotate: 135, label: 'Camshaft' });
    part('cam', 'exhaustCam', { x: 465, y: 36, size: 46, rotate: -135 });
    part('valve', 'intakeValve', { x: 395, y: 122, size: 40, params: { stem: 1.45 }, label: 'Intake valve' });
    part('valve', 'exhaustValve', { x: 465, y: 122, size: 40, params: { stem: 1.45 }, label: 'Exhaust valve' });
    part('spring', 'intakeSpring', { params: { from: [395, 70], to: [395, 106], coils: 5, width: 16 } });
    part('spring', 'exhaustSpring', { params: { from: [465, 70], to: [465, 106], coils: 5, width: 16 } });
    joint({ kind: 'cam', cam: 'intakeCam', follower: 'intakeValve', lift: 14, axis: 90, center: .125, width: .25, ratio: .5, phase: 0 });
    joint({ kind: 'cam', cam: 'exhaustCam', follower: 'exhaustValve', lift: 14, axis: 90, center: .875, width: .25, ratio: .5, phase: 0 });
    joint({ kind: 'attach', part: 'intakeSpring', end: 'from', follow: 'intakeValve' });
    joint({ kind: 'attach', part: 'exhaustSpring', end: 'from', follow: 'exhaustValve' });
  });
  beat('intake', 'First the piston drops and pulls air and fuel in through the intake valve.', () => {
    part('arrow', 'intakeFlow', { color: 'flow', params: { from: [250, 80], to: [370, 140], bend: -.2, bold: true }, label: 'Air and fuel in' });
  });
  beat('power', 'Then it squeezes the mix, the spark plug fires, and the blast shoves the piston down.', () => {
    part('box', 'plug', { x: 430, y: 100, size: 14, params: { aspect: 3, round: .3 }, label: 'Spark plug' });
    part('flame', 'flame', { x: 430, y: 148, size: 40, color: 'heat' });
  });
  beat('exhaust', 'Finally it rises again and pushes the burnt gas out through the exhaust valve.', () => {
    part('arrow', 'exhaustFlow', { color: 'heat', params: { from: [490, 140], to: [610, 80], bend: -.2, bold: true }, label: 'Burnt gas out' });
  });
};

const hover: DrawingSampleProgram = ({ card, beat, part }) => {
  card('How a jet can hover', { subtitle: 'Side view, nose on the left', takeaway: 'Up-thrust balances weight' });
  beat('jet', 'This jet can stop in mid-air and hang there.', () => {
    part('aircraft', 'jet', { x: 480, y: 250, size: 560 });
  });
  beat('fan', 'Behind the cockpit, a big fan blows air straight down.', () => {
    part('box', 'fan', { x: 380, y: 262, size: 54, params: { aspect: .8 }, color: 'flow', label: 'Lift fan' });
    part('arrow', 'fanAir', { color: 'flow', params: { from: [380, 300], to: [380, 450], bend: 0, bold: true }, label: 'Air pushed down' });
  });
  beat('nozzle', 'At the back, the engine turns its nozzle to blast down as well.', () => {
    part('box', 'engineBox', { x: 600, y: 258, size: 110, params: { aspect: .32 }, label: 'Engine' });
    part('nozzle', 'nozzle', { x: 690, y: 282, size: 46, rotate: 90, color: 'heat', label: 'Turned nozzle' });
    part('arrow', 'exhaust', { color: 'heat', params: { from: [690, 310], to: [690, 450], bend: 0, bold: true } });
  });
  beat('balance', 'Pushing air down pushes the jet up, and when that push equals its weight, it hovers.', () => {
    part('arrow', 'lift', { color: 'flow', params: { from: [520, 180], to: [520, 70], bend: 0, bold: true }, label: 'Lift' });
    part('arrow', 'weight', { params: { from: [540, 300], to: [540, 420], bend: 0 }, label: 'Weight' });
  });
};

const heart: DrawingSampleProgram = ({ card, beat, part }) => {
  card('Your heart is two pumps', { subtitle: 'Front view · your right is on the left', takeaway: 'Two pumps in one' });
  beat('heart', 'Your heart is a muscle with a wall down the middle, making two pumps side by side.', () => {
    part('heart', 'heart', { x: 480, y: 290, size: 300, color: 'power', label: 'Two sides, one wall' });
  });
  beat('right', 'The right side takes tired blood from your body and sends it to your lungs.', () => {
    part('arrow', 'fromBody', { color: 'flow', params: { from: [200, 140], to: [400, 240], bend: .15, bold: true }, label: 'From body' });
    part('arrow', 'toLungs', { color: 'flow', params: { from: [420, 200], to: [300, 70], bend: -.2, bold: true }, label: 'To lungs' });
  });
  beat('left', 'The left side takes fresh blood back from the lungs and pumps it out to your whole body.', () => {
    part('arrow', 'fromLungs', { color: 'power', params: { from: [660, 70], to: [550, 200], bend: -.2, bold: true }, label: 'From lungs' });
    part('arrow', 'toBody', { color: 'power', params: { from: [560, 250], to: [780, 160], bend: .15, bold: true }, label: 'To body' });
  });
};

const circuit: DrawingSampleProgram = ({ card, beat, part }) => {
  card('Why the bulb lights up', { subtitle: 'A simple circuit', takeaway: 'A closed loop lets current flow' });
  beat('battery', 'A battery pushes electric current out of one end and pulls it back into the other.', () => {
    part('battery', 'battery', { x: 250, y: 330, size: 150, rotate: -90, label: 'Battery' });
  });
  beat('loop', 'Wires make a loop from the battery, through a switch and a bulb, and back again.', () => {
    part('wire', 'wireTop', { params: { points: [[250, 255], [250, 150], [420, 150]] } });
    part('switch', 'switch', { x: 480, y: 150, size: 120, params: { closed: true }, label: 'Switch (closed)' });
    part('wire', 'wireRight', { params: { points: [[540, 150], [710, 150], [710, 250]] } });
    part('bulb', 'bulb', { x: 710, y: 310, size: 130, rotate: 180, params: { lit: true }, color: 'highlight', label: 'Bulb' });
    part('wire', 'wireBottom', { params: { points: [[710, 360], [710, 460], [250, 460], [250, 405]] } });
  });
  beat('current', 'With the switch closed the loop is complete, so current flows round and lights the bulb.', () => {
    part('arrow', 'currentTop', { color: 'flow', params: { from: [300, 125], to: [400, 125], bend: 0 }, label: 'Current' });
    part('arrow', 'currentBottom', { color: 'flow', params: { from: [640, 485], to: [320, 485], bend: 0 } });
  });
};

const market: DrawingSampleProgram = ({ card, beat, part }) => {
  card('Where the price settles', { subtitle: 'Supply and demand', takeaway: 'Price settles where they cross' });
  beat('axes', 'Up the side is the price, and along the bottom is how many get bought and sold.', () => {
    part('axes', 'axes', { x: 480, y: 280, size: 560, params: { aspect: .7 }, label: 'Price (up) · quantity (along)' });
  });
  beat('demand', 'When the price is high, people buy fewer, so demand slopes down.', () => {
    part('plot', 'demand', { color: 'flow', params: { points: [[240, 120], [400, 230], [560, 330], [720, 440]] }, label: 'Demand' });
  });
  beat('supply', 'When the price is high, sellers make more, so supply slopes up.', () => {
    part('plot', 'supply', { color: 'heat', params: { points: [[240, 440], [400, 330], [560, 230], [720, 120]] }, label: 'Supply' });
  });
  beat('meet', 'Where the two lines cross, buyers and sellers agree, and that is the price.', () => {
    part('circle', 'meet', { x: 480, y: 280, size: 28, color: 'highlight', label: 'Agreed price' });
  });
};

/** 3D: the same engine and the same joints, built from the parts kit. */
const engine3d: DrawingSampleProgram = ({ card, beat, part, joint }) => {
  card('Inside one engine cylinder', { subtitle: '3D · drag to turn it', takeaway: 'Suck, squeeze, bang, blow', period: 3, view: '3d' });
  beat('parts', "Here's one cylinder of a car engine, cut open so you can see inside.", () => {
    part('cylinder3d', 'cylinder', { x: 430, y: 240, size: 150, params: { depth: 1.8 }, label: 'Cylinder' });
    part('piston3d', 'piston', { x: 430, y: 215, size: 146, params: { height: .6 }, label: 'Piston' });
    part('crank3d', 'crank', { x: 430, y: 440, size: 110, params: { throw: .4 }, label: 'Crankshaft' });
    part('link3d', 'rod', { params: { from: [430, 396], to: [430, 228], width: 22 }, label: 'Connecting rod' });
    joint({ kind: 'crankSlider', crank: 'crank', slider: 'piston', link: 'rod', axis: -90, ratio: 1, phase: 0 });
  });
  beat('valves', "On top, a camshaft turning at half the crank's speed pushes two valves open in turn.", () => {
    part('shaft3d', 'camshaft', { x: 430, y: 36, size: 150, rotate: 0, yaw: 90, params: { thickness: .06 }, label: 'Camshaft' });
    part('cam3d', 'intakeCam', { x: 395, y: 36, size: 46, rotate: 135 });
    part('cam3d', 'exhaustCam', { x: 465, y: 36, size: 46, rotate: -135 });
    part('valve3d', 'intakeValve', { x: 395, y: 122, size: 40, params: { stem: 1.45 }, label: 'Intake valve' });
    part('valve3d', 'exhaustValve', { x: 465, y: 122, size: 40, params: { stem: 1.45 }, label: 'Exhaust valve' });
    part('spring3d', 'intakeSpring', { params: { from: [395, 70], to: [395, 106], coils: 5, width: 18 } });
    part('spring3d', 'exhaustSpring', { params: { from: [465, 70], to: [465, 106], coils: 5, width: 18 } });
    joint({ kind: 'cam', cam: 'intakeCam', follower: 'intakeValve', lift: 14, axis: 90, width: .25, ratio: .5, phase: 0 });
    joint({ kind: 'cam', cam: 'exhaustCam', follower: 'exhaustValve', lift: 14, axis: 90, width: .25, ratio: .5, phase: .5 });
    joint({ kind: 'attach', part: 'intakeSpring', end: 'from', follow: 'intakeValve' });
    joint({ kind: 'attach', part: 'exhaustSpring', end: 'from', follow: 'exhaustValve' });
  });
  beat('power', 'The spark fires, and the blast shoves the piston down and turns the crank.', () => {
    part('ball3d', 'blast', { x: 430, y: 146, size: 34, color: 'heat', label: 'Spark and blast' });
  });
};

const gears3d: DrawingSampleProgram = ({ card, beat, part, joint }) => {
  card('Gears trade speed for strength', { subtitle: '3D · drag to turn it', takeaway: 'Small drives big: slower, stronger', view: '3d' });
  beat('small', 'A small gear with 10 teeth is turned by the motor.', () => {
    part('gear3d', 'small', { x: 340, y: 270, size: 150, params: { teeth: 10 }, color: 'flow', label: 'Driving gear (10 teeth)' });
    joint({ kind: 'rotate', part: 'small', ratio: 1, phase: 0 });
  });
  beat('big', 'It meshes with a gear of 20 teeth, which turns half as fast but twice as hard.', () => {
    part('gear3d', 'big', { x: 548, y: 270, size: 290, params: { teeth: 20 }, label: 'Driven gear (20 teeth)' });
    joint({ kind: 'gear', driver: 'small', driven: 'big', ratio: .5 });
  });
  beat('shafts', 'Each gear sits on its own shaft.', () => {
    part('shaft3d', 'smallShaft', { x: 340, y: 270, size: 120, yaw: 90, params: { thickness: .12 } });
    part('shaft3d', 'bigShaft', { x: 548, y: 270, size: 120, yaw: 90, params: { thickness: .12 } });
  });
};

export const DRAWING_SAMPLE_PROGRAMS = { engine, hover, heart, circuit, market, engine3d, gears3d } as const;
export type DrawingSampleProgramName = keyof typeof DRAWING_SAMPLE_PROGRAMS;
